import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Users, Award, Target, Clock, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Classes from '../components/Classes';
import Contact from '../components/Contact';
import Footer from '../components/Footer';

const benefits = [
  {
    icon: <Users className="w-6 h-6" />,
    title: 'Community Spirit',
    description: 'Train together, achieve together in our motivating group environment'
  },
  {
    icon: <Award className="w-6 h-6" />,
    title: 'Expert Instruction',
    description: 'Led by certified trainers with years of experience'
  },
  {
    icon: <Target className="w-6 h-6" />,
    title: 'Structured Workouts',
    description: 'Professionally designed programs for maximum results'
  }
];

const scheduleFeatures = [
  {
    title: 'Real-Time Updates',
    description: 'View live class availability and book instantly',
    icon: Clock
  },
  {
    title: 'Easy Booking',
    description: 'Reserve your spot with just a few clicks',
    icon: Calendar
  },
  {
    title: 'Class Details',
    description: 'Get full information about each class and instructor',
    icon: Users
  }
];

export default function ClassesPage() {
  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1601422407692-ec4eeec1d9b3?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80')] bg-cover bg-center opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/50 to-black" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-7xl font-bold mb-4">GROUP FITNESS</h1>
              <p className="text-xl text-gray-400 mb-8">Transform together with our signature classes</p>
              <div className="flex flex-wrap justify-center gap-4">
                <a 
                  href="#schedule" 
                  className="bg-white text-black px-8 py-4 rounded-full font-bold hover:bg-gray-200 transition-colors"
                >
                  View Schedule
                </a>
                <a 
                  href="#contact" 
                  className="bg-white/10 backdrop-blur-sm px-8 py-4 rounded-full font-bold hover:bg-white/20 transition-colors"
                >
                  Book a Class
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2, duration: 0.8 }}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
                >
                  <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 p-4 rounded-full w-fit mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-400">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Classes Section */}
        <Classes />

        {/* Schedule Section */}
        <section id="schedule" className="py-20 bg-gradient-to-b from-gray-900 to-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl font-bold mb-4">CLASS SCHEDULE</h2>
              <p className="text-xl text-gray-400">Book your next workout session</p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="space-y-8"
              >
                <div className="grid grid-cols-1 gap-6">
                  {scheduleFeatures.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.2, duration: 0.8 }}
                      className="flex items-center space-x-4 bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
                    >
                      <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 p-3 rounded-full">
                        <feature.icon className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-1">{feature.title}</h3>
                        <p className="text-gray-400">{feature.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
              >
                <div className="text-center">
                  <h3 className="text-2xl font-bold mb-4">View Live Schedule</h3>
                  <p className="text-gray-400 mb-8">
                    Access our real-time class schedule, check availability, and book your sessions instantly through our MyWellness platform.
                  </p>
                  <a
                    href="https://widgets.mywellness.com/facility/fitnessportalakl"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-4 rounded-full font-bold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 group"
                  >
                    <span>OPEN SCHEDULE</span>
                    <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Class Etiquette Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-2xl p-8 border border-white/10"
            >
              <h2 className="text-3xl font-bold mb-6">CLASS ETIQUETTE</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold mb-4">Before Class</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li>• Book your spot in advance</li>
                    <li>• Arrive 10 minutes early</li>
                    <li>• Bring a towel and water bottle</li>
                    <li>• Wear appropriate workout attire</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-4">During Class</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li>• Follow instructor's guidance</li>
                    <li>• Respect others' space</li>
                    <li>• Clean equipment after use</li>
                    <li>• Have fun and stay motivated!</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <Contact />
      </main>

      <Footer />
    </div>
  );
}